import { Injectable } from '@angular/core';

import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';

import { BaseBetslipGroupPick } from '../../../../../core/picks/betslip-group-pick';
import { BetslipPick } from '../../../../../core/picks/betslip-pick';
import { flattenGroupPicksIfAny } from '../../../../../core/utils';
import { ComboPreventionPreCheckError } from '../../../errors/pre-check/combo-prevention-pre-check-error';
import { PreCheckErrorMessages } from '../../../errors/pre-check/pre-check-error';
import { BetbuilderV1SportComboPrevention } from '../combo-prevention/betbuilder-v1-sport-combo-prevention';
import { BetbuilderV2ParticipantComboPrevention } from '../combo-prevention/betbuilder-v2-participant-combo-prevention';
import { IComboPreventionValidator } from '../combo-prevention/combo-prevention';
import { ComboPreventionResult } from '../combo-prevention/combo-prevention-result.enum';
import { GetGroupedPicksValidators } from '../combo-prevention/group-pick-combo-prevention.service';
import { PriceBoostComboPrevention } from '../combo-prevention/priceboost-combo-prevention';
import { V1SportBetbuilderComboPrevention } from '../combo-prevention/v1-sport-betbuilder-combo-prevention';
import { V1SportComboPrevention } from '../combo-prevention/v1-sport-combo-prevention';
import { V2GolfComboPrevention } from '../combo-prevention/v2-golf-combo-prevention';
import { V2OptionMarketHorseRaceComboPrevention } from '../combo-prevention/v2-option-market-hr-combo-prevention';
import { V2OptionMarketXCastComboPrevention } from '../combo-prevention/v2-option-market-x-cast-combo-prevention';
import { V2OptionMarketXCastV1SportComboPrevention } from '../combo-prevention/v2-option-market-x-cast-vs-v1-combo-prevention';
import { V2OptionMarketXCastV2ParticipantComboPrevention } from '../combo-prevention/v2-option-market-x-cast-vs-v2-participant-combo-prevention';
import { V2ParticipantComboPrevention } from '../combo-prevention/v2-participant-combo-prevention';
import { V2ParticipantV2SportComboPrevention } from '../combo-prevention/v2-participant-v2-sport-combo-prevention';
import { V2ParticipantV1SportComboPrevention } from '../combo-prevention/v2-participant-vs-v1-combo-prevention';
import { V2SportComboPrevention } from '../combo-prevention/v2-sport-combo-prevention';
import { V2VirtualComboPrevention } from '../combo-prevention/v2-virtual-combo-prevention';
import { V1V2SportComboPrevention, V2V1SportComboPrevention } from '../combo-prevention/v2-vs-v1-sport-combo';

/**
 * Validates if 2 picks are compatible for combo bet.
 */
@Injectable()
export class ComboPreventionValidator {
    constructor(private betslipConfig: BetslipConfig) {}
    _validators = this.getRegisteredComboValidators();

    private getRegisteredComboValidators(): IComboPreventionValidator[] {
        return [
            new V1SportComboPrevention(),
            new V2SportComboPrevention(),
            new V2V1SportComboPrevention(),
            new V1V2SportComboPrevention(),
            new V2ParticipantComboPrevention(),
            new V2OptionMarketHorseRaceComboPrevention(),
            new V2OptionMarketXCastComboPrevention(),
            new V2OptionMarketXCastV1SportComboPrevention(),
            new V2OptionMarketXCastV2ParticipantComboPrevention(),
            new V2VirtualComboPrevention(this.betslipConfig),
            new V2GolfComboPrevention(),
            new V2ParticipantV1SportComboPrevention(),
            new PriceBoostComboPrevention(),
            new V1SportBetbuilderComboPrevention(),
            new BetbuilderV1SportComboPrevention(),
            new V2ParticipantV2SportComboPrevention(),
            new BetbuilderV2ParticipantComboPrevention(),
            ...GetGroupedPicksValidators(),
        ];
    }

    private getMessageKey(result: ComboPreventionResult): keyof PreCheckErrorMessages {
        switch (result) {
            case ComboPreventionResult.SameEvent:
            case ComboPreventionResult.SameEventGroup:
                return 'ComboPreventionSameEvent';
            case ComboPreventionResult.SystemCombinationForecast:
                return 'ForecastSystemBet';
            default:
                return 'ComboPrevention';
        }
    }

    getRestrictions(picks: BetslipPick[], isSystem: boolean, ignoreGroupPicks: boolean): Record<string, Record<string, ComboPreventionResult>> {
        if (!picks.length) {
            return {};
        }
        const result: Record<string, Record<string, ComboPreventionResult>> = {};
        for (const thisPick of picks) {
            for (const otherPick of picks) {
                if (otherPick.id.toString() === thisPick.id.toString()) {
                    continue;
                }
                const comboPreventionResult = this.validatePicks(thisPick, otherPick, isSystem, ignoreGroupPicks);
                if (comboPreventionResult !== ComboPreventionResult.AllCombo) {
                    result[thisPick.id.toString()] = {
                        ...(result[thisPick.id.toString()] || {}),
                        [otherPick.id.toString()]: comboPreventionResult,
                    };
                }
                this.validateChildPicksIfAny(thisPick, otherPick, isSystem, result, ignoreGroupPicks);
            }
        }

        return result;
    }

    //Combo checks will be executed outside OGP groups as normal (each pick within OGP to any other pick outside of the same OGP group)
    validateChildPicksIfAny(
        firstPick: BetslipPick,
        secondPick: BetslipPick,
        isSystem: boolean,
        result: Record<string, Record<string, ComboPreventionResult>>,
        ignoreGroupPicks: boolean,
    ) {
        const isFirstPickGroup = BaseBetslipGroupPick.isPick(firstPick);
        const isSecondPickAGroup = BaseBetslipGroupPick.isPick(secondPick);
        //if neither picks is a group pick, don't try to validate any child picks
        if (!isFirstPickGroup && !isSecondPickAGroup) {
            return;
        }
        const firstPickList = flattenGroupPicksIfAny([firstPick]);
        const secondPickList = flattenGroupPicksIfAny([secondPick]);

        for (const pick of firstPickList) {
            for (const theOtherPick of secondPickList) {
                const childComboPreventionResult = this.validatePicks(pick, theOtherPick, isSystem, ignoreGroupPicks);
                if (childComboPreventionResult !== ComboPreventionResult.AllCombo) {
                    result[pick.id.toString()] = {
                        ...(result[pick.id.toString()] || {}),
                        [theOtherPick.id.toString()]: childComboPreventionResult,
                    };
                    result[firstPick.id.toString()] = {
                        ...(result[firstPick.id.toString()] || {}),
                        [theOtherPick.id.toString()]: childComboPreventionResult,
                    };
                }
            }
        }
    }

    validatePicks(thisPick: BetslipPick, otherPick: BetslipPick, isSystem: boolean, ignoreGroupPicks: boolean) {
        const validators = this._validators;

        const activeValidators = validators.filter((v) => v.canValidate(thisPick, otherPick, isSystem || ignoreGroupPicks));
        for (const validator of activeValidators) {
            const comboPreventionResult = validator.validate(thisPick, otherPick, isSystem);
            if (comboPreventionResult !== ComboPreventionResult.AllCombo) {
                return comboPreventionResult;
            }
        }

        return ComboPreventionResult.AllCombo;
    }

    convertRestrictionsToErrors(restrictions: Record<string, Record<string, ComboPreventionResult>>): ComboPreventionPreCheckError[] {
        const result: ComboPreventionPreCheckError[] = [];
        for (const [key, value] of Object.entries(restrictions)) {
            const firstResult = Object.values(value)[0];

            const error = new ComboPreventionPreCheckError(key, this.getMessageKey(firstResult));

            error.incompatiblePicks = value;

            result.push(error);
        }

        return result;
    }
}
